import React, { useState } from 'react';

import { ScreenHead, Rise } from '../ui/screen-parts.js';
import { Chip, Modal, useFlash, useModal } from '../ui/kit.js';
import { Done, Stepper, useLast } from '../ui/actions/parts.js';
import { day } from '../state/building.js';

import MorningSwim from '../media/images/pool-screen/morning-swim.jpg';
import EveningRelaxation from '../media/images/pool-screen/evening-relaxation.jpg';
import BeachLounge from '../media/images/pool-screen/beachside-lounge.jpg';
import BeachParty from '../media/images/pool-screen/beach-party.jpg';
import SunsetYoga from '../media/images/pool-screen/sunset-yoga.jpg';
import Volleyball from '../media/images/pool-screen/beach-volleyball-tournament.jpg';
import MovieNight from '../media/images/pool-screen/poolside-movie-night.jpg';
import Kayaking from '../media/images/pool-screen/kayaking-adventure.jpg';
import Bonfire from '../media/images/pool-screen/beach-bonfire-night.jpeg';

/* The next time something happens: `weekday` is 0 for Sunday, or null
   for something that runs every day (and so is next on tomorrow). */
function next(weekday, time) {
    if (weekday === null) return `Tomorrow, ${time}`;
    const today = new Date().getDay();
    const offset = (weekday - today + 7) % 7 || 7;
    return `${offset === 1 ? 'Tomorrow' : day(offset)}, ${time}`;
}

/* `kind` decides the verb: a lane or a daybed is reserved, a party is
   RSVPed. `capacity` and `taken` are the next session's numbers. */
const SERVICES = [
    {
        name: "Morning Swim at the Pool",
        description: "Enjoy a peaceful swim from 6 AM to 9 AM. Adults only during this time.",
        imageUrl: MorningSwim,
        when: 'Daily, 6 to 9 AM', where: 'Oceanfront lap pool, level 5', start: next(null, '6:00 AM'),
        capacity: 12, taken: 7, unit: 'lanes', kind: 'reserve', noun: 'lane',
        includes: ['Roped lap lanes', 'Towels and lockers', 'Adults only']
    },
    {
        name: "Evening Relaxation at the Pool",
        description: "Unwind by the pool with ambient lighting from 6 PM to 9 PM. Families welcome.",
        imageUrl: EveningRelaxation,
        when: 'Daily, 6 to 9 PM', where: 'Main pool deck, level 5', start: next(null, '6:00 PM'),
        capacity: 40, taken: 22, unit: 'loungers', kind: 'reserve', noun: 'lounger',
        includes: ['Ambient lighting', 'Poolside bar service', 'Families welcome']
    },
    {
        name: "Beachside Lounge",
        description: "Relax at our private beach lounge, open daily from 10 AM to 6 PM. Umbrellas and towels provided.",
        imageUrl: BeachLounge,
        when: 'Daily, 10 AM to 6 PM', where: 'Private beach, east gate', start: next(null, '10:00 AM'),
        capacity: 24, taken: 15, unit: 'daybeds', kind: 'reserve', noun: 'daybed',
        includes: ['Umbrella and towels', 'Beach butler service', 'Chilled water and fruit']
    },
    {
        name: "Beach Party",
        description: "Join our weekly beach party every Saturday at 5 PM. Live music, drinks, and games included. RSVP required.",
        imageUrl: BeachParty,
        when: 'Saturdays, 5 PM', where: 'Private beach', start: next(6, '5:00 PM'),
        capacity: 120, taken: 86, unit: 'guests', kind: 'rsvp',
        includes: ['Live music', 'Open bar', 'Lawn games', 'RSVP required']
    },
    {
        name: "Sunset Yoga on the Beach",
        description: "Wind down with a sunset yoga session on the beach every Friday at 6 PM.",
        imageUrl: SunsetYoga,
        when: 'Fridays, 6 PM', where: 'Beach, north dune', start: next(5, '6:00 PM'),
        capacity: 20, taken: 14, unit: 'mats', kind: 'rsvp',
        includes: ['Mats and blocks', 'All levels', '60 minutes']
    },
    {
        name: "Beach Volleyball Tournament",
        description: "Join the community beach volleyball tournament every Sunday at 3 PM.",
        imageUrl: Volleyball,
        when: 'Sundays, 3 PM', where: 'Beach courts', start: next(0, '3:00 PM'),
        capacity: 32, taken: 26, unit: 'players', kind: 'rsvp',
        includes: ['Teams of four', 'Referee on court', 'Trophy for the winners']
    },
    {
        name: "Poolside Movie Night",
        description: "Enjoy a family-friendly movie by the pool every Thursday at 7 PM.",
        imageUrl: MovieNight,
        when: 'Thursdays, 7 PM', where: 'Pool deck screen', start: next(4, '7:00 PM'),
        capacity: 60, taken: 31, unit: 'seats', kind: 'rsvp',
        includes: ['Popcorn and sodas', 'Blankets', 'Family friendly']
    },
    {
        name: "Kayaking Adventure",
        description: "Experience a guided kayaking adventure along the coastline every Saturday at 9 AM.",
        imageUrl: Kayaking,
        when: 'Saturdays, 9 AM', where: 'Beach launch, south end', start: next(6, '9:00 AM'),
        capacity: 12, taken: 10, unit: 'kayaks', kind: 'reserve', noun: 'kayak',
        includes: ['Guide and safety briefing', 'Life vests', 'Dry bags']
    },
    {
        name: "Beach Bonfire Night",
        description: "Join us for a beach bonfire with s'mores and music every Wednesday at 8 PM.",
        imageUrl: Bonfire,
        when: 'Wednesdays, 8 PM', where: 'Beach fire pit', start: next(3, '8:00 PM'),
        capacity: 50, taken: 27, unit: 'guests', kind: 'rsvp',
        includes: ["S'mores kits", 'Acoustic music', 'Blankets']
    }
];

const verb = (s, n) => (s.kind === 'rsvp'
    ? `RSVP for ${n}`
    : `Reserve ${n} ${n === 1 ? s.noun : `${s.noun}s`}`);

function PoolScreen()
{
    const [flash, notify] = useFlash();
    const [going, setGoing] = useState({});   // name → party size
    const sheet = useModal();

    const join = (service, party) => setGoing(g => ({ ...g, [service.name]: party }));

    const leave = service => {
        setGoing(g => {
            const rest = { ...g };
            delete rest[service.name];
            return rest;
        });
        sheet.close();
        notify(`${service.name}: ${service.kind === 'rsvp' ? 'RSVP' : 'reservation'} cancelled. Your spot is open for a neighbor.`);
    };

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Amenities" title="Pool & beach" />
            {flash}

            <div className="pool-grid">
                {SERVICES.map((service, index) => {
                    const party = going[service.name];
                    const left = service.capacity - service.taken - (party || 0);

                    return (
                        <Rise className={`pool-beach-service-card${party ? ' pool-card--going' : ''}`} key={service.name} index={index}>
                            <img src={service.imageUrl} alt="" className="pool-beach-service-image" loading="lazy" />
                            <div className="pool-beach-service-details">
                                <h4>{service.name}</h4>
                                <p>{service.description}</p>
                            </div>
                            <div className="pool-card__chips">
                                {party
                                    ? <Chip tone="ok">{service.kind === 'rsvp' ? 'Going' : 'Reserved'} · {party}</Chip>
                                    : <Chip tone={left <= 3 ? 'warn' : 'muted'}>{left} {service.unit} left</Chip>}
                                <Chip tone="muted">{service.when}</Chip>
                            </div>
                            <button type="button" className="btn btn-outline-light" onClick={() => sheet.open(service)}>
                                {party ? (service.kind === 'rsvp' ? 'View your RSVP' : 'View your reservation') : 'Learn more'}
                            </button>
                        </Rise>
                    );
                })}
            </div>

            <PoolSheet
                item={sheet.item}
                party={sheet.item ? going[sheet.item.name] : undefined}
                onClose={sheet.close}
                onJoin={join}
                onLeave={leave}
            />
        </div>
    );
}

function PoolSheet({ item, party, onClose, onJoin, onLeave }) {
    const shown = useLast(item);
    const [openFor, setOpenFor] = useState(null);
    const [size, setSize] = useState(2);
    const [justJoined, setJustJoined] = useState(false);

    if (item !== openFor) {
        setOpenFor(item);
        if (item) {
            setSize(party || (item.kind === 'reserve' ? 1 : 2));
            setJustJoined(false);
        }
    }

    if (!shown) return <Modal open={false} onClose={onClose} />;
    const s = shown;
    const taken = s.taken + (party || 0);
    const left = s.capacity - taken;
    const max = Math.max(1, Math.min(6, left));
    const pct = Math.round((taken / s.capacity) * 100);

    const join = () => {
        onJoin(s, size);
        setJustJoined(true);
    };

    let actions;
    if (justJoined) {
        actions = <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>;
    } else if (party) {
        actions = (
            <>
                <button type="button" className="btn btn-outline-light" onClick={() => onLeave(s)}>
                    {s.kind === 'rsvp' ? 'Cancel RSVP' : 'Cancel reservation'}
                </button>
                <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
            </>
        );
    } else {
        actions = (
            <>
                <button type="button" className="btn btn-outline-light" onClick={onClose}>Close</button>
                <button type="button" className="btn btn-primary" onClick={join} disabled={left <= 0}>
                    {left <= 0 ? 'Full' : verb(s, size)}
                </button>
            </>
        );
    }

    return (
        <Modal open={Boolean(item)} onClose={onClose} eyebrow="Pool & beach" title={s.name} actions={actions}>
            {justJoined ? (
                <Done title={s.kind === 'rsvp' ? 'You are on the list' : 'Reserved'}>
                    <p>
                        {s.name}, {s.start.replace(/^Tomorrow/, 'tomorrow')}, {s.where.toLowerCase().startsWith('private') ? 'on the ' : 'at the '}
                        {s.where.charAt(0).toLowerCase() + s.where.slice(1)}.
                        {s.kind === 'rsvp'
                            ? ` Party of ${size}. The pool attendant will have your names at the gate.`
                            : ` ${size} ${size === 1 ? s.noun : `${s.noun}s`} held in your name. Check in with the attendant.`}
                    </p>
                </Done>
            ) : (
                <>
                    <img className="modal__media" src={s.imageUrl} alt={s.name} />
                    <p>{s.description}</p>

                    <dl className="act-facts">
                        <div><dt>When</dt><dd>{s.when}</dd></div>
                        <div><dt>Next</dt><dd>{s.start}</dd></div>
                        <div className="act-facts__wide"><dt>Where</dt><dd>{s.where}</dd></div>
                        <div className="act-facts__wide">
                            <dt>Capacity</dt>
                            <dd>{taken} of {s.capacity} {s.unit} taken · {left} left</dd>
                            <div className={`meter${pct >= 90 ? ' meter--alert' : pct >= 75 ? ' meter--warn' : ''}`} style={{ marginTop: '0.45rem' }}>
                                <span className="meter__fill" style={{ width: `${pct}%` }} />
                            </div>
                        </div>
                    </dl>

                    <div className="row__end" style={{ justifyContent: 'flex-start' }}>
                        {s.includes.map(text => <Chip key={text} tone="info">{text}</Chip>)}
                    </div>

                    {party ? (
                        <p className="availability-message">
                            {s.kind === 'rsvp' ? `You are going, party of ${party}.` : `${party} ${party === 1 ? s.noun : `${s.noun}s`} reserved in your name.`}
                            {' '}See you {s.start.startsWith('Tomorrow') ? s.start.replace('Tomorrow', 'tomorrow') : `on ${s.start}`}.
                        </p>
                    ) : left > 0 && (
                        <div className="pool-party">
                            <span className="form-label" style={{ margin: 0 }}>{s.kind === 'rsvp' ? 'Guests, including you' : `${s.noun.charAt(0).toUpperCase()}${s.noun.slice(1)}s`}</span>
                            <Stepper value={size} onChange={setSize} min={1} max={max} label={s.kind === 'rsvp' ? 'Guests' : `${s.noun}s`} />
                        </div>
                    )}
                </>
            )}
        </Modal>
    );
}

export default PoolScreen;
